/**
 * Order Summary — sticky right column on /portal/commission.
 *
 * Renders the server-authoritative cart breakdown from
 * /api/v1/client-portal/pricing/preview. Line items come straight
 * from the server response — the SPA never invents pricing copy, so
 * what's shown here is exactly what Stripe will charge.
 *
 * Below the lines:
 *   - Bill-to row (authenticated client identity, read-only)
 *   - Pay button (enabled only when canSubmit())
 *   - Blocker list (what's still missing) when disabled
 *   - Submission error when the checkout call fails
 *   - Custom Inquiry expander — "Doesn't fit? →" — opens an inline
 *     panel adjacent to Pay per the research recommendation (§5.5)
 *
 * The Pay button is the only commit point; everything above it can
 * be edited until the click.
 */

import { useState } from "react";

import type { CartBreakdown } from "../api";
import { getSubmitBlockers } from "../selectors";
import { canSubmit, type ReconConfiguratorState } from "../state";
import { buildCartFromState } from "../state";
import { CustomInquiryPanel } from "./CustomInquiryPanel";

import "./order-summary.css";

interface ClientProfileShape {
  display_name: string;
  contact_name: string;
  contact_email: string | null;
  company: string | null;
}

interface Props {
  state: ReconConfiguratorState;
  /** Authenticated client profile from `useClientAuth().me`. Null
   *  during auth bootstrap; CommissionPage guards on isBootstrapping
   *  so by the time this renders, `me` should be present. */
  me: ClientProfileShape | null;
  onSubmit: () => void | Promise<void>;
  /** Back-to-dashboard. Wired by CommissionPage so OrderSummary
   *  doesn't import useNavigate. */
  onBack: () => void;
}

export function OrderSummary({ state, me, onSubmit, onBack }: Props) {
  const quote = state.quote;
  const submission = state.submission;
  const blockers = getSubmitBlockers(state);
  const canPay =
    canSubmit(state) &&
    submission.kind !== "loading" &&
    submission.kind !== "redirecting";

  const [inquiryOpen, setInquiryOpen] = useState(false);

  return (
    <aside className="order-summary" aria-labelledby="order-summary-heading">
      <header className="order-summary-header">
        <button
          type="button"
          className="order-summary-back"
          onClick={onBack}
          aria-label="Back to dashboard"
        >
          ← Dashboard
        </button>
        <h2 id="order-summary-heading">Your scope</h2>
      </header>

      {quote.kind === "ok" ? (
        <BreakdownLines breakdown={quote.data} />
      ) : (
        <PlaceholderCart kind={quote.kind} state={state} />
      )}

      <PayButton
        canPay={canPay}
        submissionKind={submission.kind}
        onClick={() => void onSubmit()}
      />

      <p className="order-summary-finefoot">
        Secured by Stripe · 7-day refund if we can't deliver.
      </p>

      {/* Blockers — only when Pay is disabled and we have data to show. */}
      {blockers.length > 0 &&
        submission.kind !== "loading" &&
        submission.kind !== "redirecting" && (
          <ul className="order-summary-blockers">
            {blockers.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        )}

      {submission.kind === "error" && (
        <p className="order-summary-error" role="alert">
          {submission.message}
        </p>
      )}

      {/* Custom Inquiry — the escape hatch lives adjacent to Pay so
       *  buyers who realize Quick Check doesn't fit have a one-click
       *  exit ramp instead of bouncing. Closed by default; small
       *  text-link to open. */}
      <div className="order-summary-inquiry-row">
        <button
          type="button"
          className="order-summary-inquiry-toggle"
          onClick={() => setInquiryOpen((v) => !v)}
          aria-expanded={inquiryOpen}
          aria-controls="custom-inquiry-panel"
        >
          {inquiryOpen ? "Hide custom inquiry" : "Doesn't fit? Custom inquiry →"}
        </button>
      </div>
      {inquiryOpen && (
        <CustomInquiryPanel
          me={me}
          attemptedCart={buildCartFromState(state)}
          onClose={() => setInquiryOpen(false)}
        />
      )}
    </aside>
  );
}

// --------------------------------------------------------------------------- //
// Sub-components                                                              //
// --------------------------------------------------------------------------- //

function BreakdownLines({ breakdown }: { breakdown: CartBreakdown }) {
  return (
    <ul className="order-summary-lines">
      {breakdown.line_items.map((li, i) => (
        <li key={i} className="order-summary-line">
          <span className="order-summary-line-main">
            <span className="order-summary-line-label">{li.label}</span>
            {li.detail && (
              <span className="order-summary-line-detail">{li.detail}</span>
            )}
          </span>
          <span className="order-summary-line-amount">
            {li.amount_cents === 0
              ? "Included"
              : `$${(li.amount_cents / 100).toFixed(2)}`}
          </span>
        </li>
      ))}
      <li className="order-summary-total">
        <span>Total today</span>
        <span>${(breakdown.subtotal_cents / 100).toFixed(2)}</span>
      </li>
    </ul>
  );
}

/** Empty / loading / error states for the summary panel.
 *
 *  The idle state (no primary domain yet) describes what the
 *  engagement IS instead of showing a worked-example price — the
 *  buyer is signed in, what they need at this moment is to
 *  understand the rate structure and what their first scoping
 *  decision will do, not a fake $294 number to anchor against. */
function PlaceholderCart({
  kind,
  state,
}: {
  kind: "idle" | "loading" | "error";
  state: ReconConfiguratorState;
}) {
  if (kind === "loading") {
    return <p className="order-summary-placeholder">Updating your price…</p>;
  }
  if (kind === "error") {
    return (
      <p className="order-summary-placeholder">
        Couldn't price the scope. Adjust your selection and we'll retry
        automatically.
      </p>
    );
  }
  // idle = no primary domain entered yet. Describe the engagement
  // shape so the buyer knows what each scoping decision does to the
  // total before they make one.
  if (state.primaryDomain.trim().length === 0) {
    return (
      <div className="order-summary-shape">
        <p className="order-summary-shape-lede">
          Your engagement starts at <strong>$249</strong>: one primary
          domain, fully investigated, with five subdomains of your
          choosing.
        </p>
        <ul className="order-summary-shape-lines">
          <li>
            <span className="order-summary-shape-key">+ subdomain</span>
            <span className="order-summary-shape-val">$15 each</span>
          </li>
          <li>
            <span className="order-summary-shape-key">+ domain or IP</span>
            <span className="order-summary-shape-val">$175 each</span>
          </li>
        </ul>
        <p className="order-summary-shape-foot">
          Enter your apex domain on the left to start scoping.
        </p>
      </div>
    );
  }
  return (
    <p className="order-summary-placeholder">
      Scan your apex to see what's reachable, then pick what's in scope.
    </p>
  );
}

interface PayButtonProps {
  canPay: boolean;
  submissionKind: ReconConfiguratorState["submission"]["kind"];
  onClick: () => void;
}

function PayButton({ canPay, submissionKind, onClick }: PayButtonProps) {
  return (
    <button
      type="button"
      className="order-summary-pay-btn"
      onClick={onClick}
      disabled={!canPay}
    >
      {submissionKind === "loading"
        ? "Starting checkout…"
        : submissionKind === "redirecting"
          ? "Redirecting to payment…"
          : "Pay with card"}
    </button>
  );
}
